<template>
    <DsModal
        :show-close="!isTestEmail"
        :show-back="isTestEmail"
        :is-open="isOpen"
        :title="modalTitle"
        class="validated-domains-modal"
        @close="$emit('close')"
        @back="$emit('close')"
    >
        <template #default>
            <section v-if="domains.length">
                <DsInputField
                    v-model="displayName"
                    class="validated-domains-displayName-input"
                    data-qa="validated-domains-displayName-input"
                    required
                    type="text"
                    :invalid="!isDisplayNameValid"
                    :maxlength="displayNameMaxlength"
                    :submitted="submitted"
                    :label="$t('email.broadcast.validated.domains.modal.displayName')"
                    trim
                >
                    <template #error>
                        <small>
                            {{ $t('email.broadcast.validated.domains.error.displayName') }}
                        </small>
                    </template>
                </DsInputField>

                <div class="email-address-container">
                    <DsInputField
                        v-model="emailUsername"
                        class="validated-domains-emailUsername-input grid-item"
                        data-qa="validated-domains-emailUsername-input"
                        required
                        type="text"
                        :maxlength="emailUsernameMaxlength"
                        :invalid="!isEmailUserNameValid || showUsernameError"
                        :submitted="submitted"
                        :label="$t('email.broadcast.validated.domains.modal.emailUsername')"
                        trim
                    >
                        <template #error>
                            <small>
                                {{ emailUsernameError }}
                            </small>
                        </template>
                    </DsInputField>

                    <DsMultiselect
                        v-model="selectedDomain"
                        class="validated-domains-emailDomain-input grid-item"
                        data-qa="validated-domains-emailDomain-input"
                        allow-add
                        required
                        :force-invalid="submitted && !isDomainValid"
                        :options="domains"
                        :placeholder="$t('email.broadcast.validated.domains.modal.emailDomain')"
                        :add-text="$t('email.broadcast.validated.domains.modal.addEmailDomain')"
                        @add="handleAddCustomDomain"
                    >
                        <template #help>
                            <small v-if="submitted && !isDomainValid">
                                {{ $t('email.broadcast.validated.domains.error.emailDomain') }}
                            </small>
                        </template>
                    </DsMultiselect>
                </div>

                <div class="validated-footer-domains-description" data-qa="existing-domains-description">
                    {{ $t('email.broadcast.validated.domains.modal.description') }}

                    <a
                        :href="learnMoreLink"
                        target="_blank"
                        rel="noopener noreferrer"
                        data-qa="existing-learn-more"
                    >
                        {{ $t('email.broadcast.validated.domains.modal.learnMore') }}
                    </a>
                </div>

                <ds-filled-button
                    data-qa="validated-domains-modal-save-button"
                    @click="handleCustomSenderSave"
                >
                    {{ $t('global.save') }}
                </ds-filled-button>
            </section>

            <section v-else>
                <p class="action-domain-needed">
                    {{ $t('email.broadcast.validated.domains.modal.actionNeeded') }}
                </p>

                <p class="action-domain-footer">
                    {{ $t('email.broadcast.validated.domains.modal.noValidateDomains') }}
                </p>

                <ds-filled-button
                    class="set-up-button"
                    as="a"
                    :href="setUpLink"
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    {{ $t('email.broadcast.validated.domains.modal.setUp') }}
                </ds-filled-button>

                <ds-text-button
                    as="a"
                    :href="learnMoreLink"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-qa="validated-domains-modal-learn-more"
                >
                    {{ $t('email.broadcast.validated.domains.modal.learnMore') }}
                </ds-text-button>
            </section>
        </template>
    </DsModal>
</template>

<script>
import {
    DKIM_LEARN_MORE_LINK_ALL_EDITIONS,
    DKIM_SET_UP_LINK_NON_SPA,
    CUSTOM_SENDER_DISPLAY_NAME_MAX_LENGTH,
    CUSTOM_SENDER_EMAIL_USERNAME_MAX_LENGTH,
} from '@/components/broadcast/constants/emailBroadcast.constants';
import { isValidEmailAddress } from '@/utils/email.util';

export default {
    name: 'EmailValidatedDomainsModal',

    props: {
        domains: {
            type: Array,
            required: true,
        },
        isOpen: Boolean,
        isSpaApp: {
            type: Boolean,
            default: false,
        },
        environment: {
            type: String,
            default: 'Production',
        },
        showVerifiedDomainsNewKeysOnlyEnabled: Boolean,
        isTestEmail: Boolean,
    },

    data() {
        return {
            displayName: '',
            emailUsername: '',
            selectedDomain: {},
            submitted: false,
            showUsernameError: false,
        };
    },

    watch: {
        emailUsername() {
            this.showUsernameError = false;
        },
    },

    computed: {
        isDisplayNameValid() {
            return !!this.displayName.length;
        },

        isEmailUserNameValid() {
            const emailUsername = this.emailUsername.trim();

            return new RegExp(/^[^@]+$/).test(emailUsername);
        },

        isDomainValid() {
            return !!this.selectedDomain.value;
        },

        emailUsernameError() {
            if (!this.emailUsername.length) {
                return this.$t('email.broadcast.validated.domains.error.emailUsername');
            }

            return this.$t('email.broadcast.validated.domains.error.invalidEmailUsername');
        },

        isFullEmailAddressValid() {
            const emailUsername = this.emailUsername.trim();
            const emailDomain = this.selectedDomain?.value;
            const fullEmailAddress = `${emailUsername}${emailDomain}`;

            return isValidEmailAddress(fullEmailAddress);
        },

        isValidSender() {
            return this.isDisplayNameValid && this.isEmailUserNameValid && this.isFullEmailAddressValid && this.isDomainValid;
        },

        setUpLink() {
            if (this.isSpaApp) {
                const path = '/settings/domains';

                switch (this.environment.toLowerCase()) {
                case 'production':
                default:
                    return `https://keap.app${path}`;
                case 'development':
                case 'sandbox':
                    return `http://localhost:10239${path}`;
                case 'integration':
                    return `https://integration.keap.app${path}`;
                case 'proofing':
                case 'staging':
                    return `https://staging.keap.app${path}`;
                }
            }

            return DKIM_SET_UP_LINK_NON_SPA;
        },

        learnMoreLink() {
            return DKIM_LEARN_MORE_LINK_ALL_EDITIONS;
        },

        displayNameMaxlength() {
            return CUSTOM_SENDER_DISPLAY_NAME_MAX_LENGTH;
        },

        emailUsernameMaxlength() {
            return CUSTOM_SENDER_EMAIL_USERNAME_MAX_LENGTH;
        },

        modalTitle() {
            if (this.domains) {
                return this.$t('email.broadcast.validated.domains.modal.title');
            }

            return this.$t('email.broadcast.validated.domains.modal.title.sender');
        },
    },

    methods: {
        handleAddCustomDomain() {
            window.open(this.setUpLink, '_blank');
        },

        handleCustomSenderSave() {
            this.submitted = true;

            if (!this.isFullEmailAddressValid) {
                this.showUsernameError = true;
            }

            if (!this.isValidSender) {
                return;
            }

            const customSender = {
                displayName: this.displayName,
                emailUsername: this.emailUsername,
                selectedDomain: this.selectedDomain,
            };

            this.$emit('custom-sender-selected', customSender);
            this.$emit('close');
        },
    },
};
</script>

<style lang="scss" scoped>
.validated-domains-modal {
    :deep(.modal-content-wrapper),
    :deep(.modal-header) {
        border-radius: $spacing-150;
    }
    --modal-overflow: visible;
}

.email-address-container {
    display: grid;
    grid-column-gap: $spacing-100;
    grid-template-columns: 40% 60%;
    padding-right: $spacing-075;
    margin-bottom: $spacing-100;
}

.grid-item {
    margin: 0;
}

.validated-domains-displayName-input {
    --input-padding-top: 0;
    --input-padding-bottom: 0;
}

.validated-domains-emailUsername-input {
    grid-column: 1;
    --input-padding-top: 0;
    --input-padding-bottom: 0;
}

.validated-domains-emailDomain-input {
    grid-column: 2;
}

.validated-footer-domains-description {
    color: $color-text-subtle;
    margin-bottom: $spacing-300;
    line-height: $line-height-lg;
}

.set-up-button {
    margin-right: $spacing-100;
}

.action-domain-needed {
    font-size: $font-size-lg;
    font-weight: $font-weight-bold;
    line-height: $line-height-lg;
}
.action-domain-footer {
    margin-bottom: $spacing-300;
}
</style>
